import { useAdminRole } from "@/hooks/useAdminRole";
import { useHousehold } from "@/hooks/useHousehold";
import { useQaTestAccess } from "@/hooks/useQaTestAccess";

export type AccessTier = "admin" | "paid" | "none";

/**
 * Unified access gate for the PAID product. Resolution order is sacred:
 *   1) admin — full unrestricted internal access (role-table backed)
 *   2) paid  — active subscription, billing-exempt account, parent of a
 *              household, or an explicit internal QA allowlist identity
 *   3) none  — no entitlement; the Stripe paywall takes over
 *
 * There is no customer-facing beta tier. Internal QA access is intentional
 * internal authorization (`useQaTestAccess`, allowlist + role backed) and is
 * never offered as a customer path.
 */
export function useAccessTier(): {
  tier: AccessTier;
  loading: boolean;
  isAdmin: boolean;
  hasPaidAccess: boolean;
  /** Internal QA/service identity — NOT a customer access path. */
  isInternalQa: boolean;
} {
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const { hasActiveSubscription, billingExempt, loading: householdLoading } = useHousehold();
  const { isQaTestAllowlisted, loading: qaLoading } = useQaTestAccess();

  // Internal identities resolve first and short-circuit; everyone else waits
  // for server-authoritative billing state before the gate decides.
  const loading = qaLoading
    ? true
    : isQaTestAllowlisted
      ? false
      : adminLoading || householdLoading;

  // Paid-only: being a household parent is NOT entitlement on its own, because
  // any signed-up account bootstraps a household. Entitlement comes from an
  // active subscription, a billing-exempt record, or an internal QA identity.
  const hasPaidAccess = hasActiveSubscription || billingExempt || isQaTestAllowlisted;




  const base = { loading, isAdmin, hasPaidAccess, isInternalQa: isQaTestAllowlisted };

  if (isAdmin) return { ...base, tier: "admin" };
  if (hasPaidAccess) return { ...base, tier: "paid" };
  return { ...base, tier: "none" };
}
